import { useState, useEffect, useMemo } from 'react';
import { EmptyState, FileToDataURL, PrimaryButton, SecondaryButton } from '@/lib';
import { Gated } from '@/app/CapabilitiesProvider';
import type { ApiClients } from '@/api/client';
import { useOrg } from '@/screens/S13Users/queries';
import { usePatchOrg } from './queries';

export type S16OrgSettingsProps = { clients: ApiClients };

// Known flag keys we surface as boolean toggles in the UI. New flags
// added server-side without being listed here are still persisted by
// the mutation (since we send the entire feature_flags object) but
// won't appear as a toggle until added below.
const KNOWN_FLAGS: Array<{ key: string; label: string; help: string }> = [
  {
    key: 'beta_fft_workbench',
    label: 'FFT Workbench (beta)',
    help: 'Opt-in to the new current-vs-baseline overlay surface.',
  },
  {
    key: 'beta_radiant_portal',
    label: 'Radiant tenant portal',
    help: 'Show the multi-tenant admin entry point in the app shell.',
  },
];

/**
 * S16 Org Settings. Read view for everyone; admins (cap org.edit)
 * get inline forms for the four override slices.
 */
export function S16OrgSettings({ clients }: S16OrgSettingsProps) {
  const org = useOrg(clients);
  const patch = usePatchOrg(clients);
  const [editing, setEditing] = useState(false);

  // Local form state. Hydrated from server data on every fresh fetch
  // and reset by Cancel.
  const [displayName, setDisplayName] = useState('');
  const [logo, setLogo] = useState('');
  const [timezone, setTimezone] = useState('');
  const [locale, setLocale] = useState('');
  const [flags, setFlags] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!org.data) return;
    setDisplayName(org.data.display_name ?? '');
    setLogo(org.data.logo_data_url ?? '');
    setTimezone(org.data.timezone ?? '');
    setLocale(org.data.locale ?? '');
    setFlags(org.data.feature_flags ?? {});
  }, [org.data]);

  const knownFlagPairs = useMemo(() => {
    return KNOWN_FLAGS.map((f) => ({ ...f, enabled: !!flags[f.key] }));
  }, [flags]);

  if (org.isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load org"
        body={String(org.error)}
        cta={{ label: 'Retry', onClick: () => org.refetch() }}
      />
    );
  }

  const cancel = () => {
    if (!org.data) return;
    setDisplayName(org.data.display_name ?? '');
    setLogo(org.data.logo_data_url ?? '');
    setTimezone(org.data.timezone ?? '');
    setLocale(org.data.locale ?? '');
    setFlags(org.data.feature_flags ?? {});
    setEditing(false);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    patch.mutate(
      {
        display_name: displayName,
        logo_data_url: logo,
        timezone,
        locale,
        feature_flags: flags,
      },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <div className="flex flex-col gap-sp-6">
      <header className="flex items-center justify-between gap-sp-4">
        <div className="flex items-center gap-sp-3">
          {org.data?.logo_data_url ? (
            <img
              src={org.data.logo_data_url}
              alt=""
              className="h-14 w-14 rounded-r-default border border-gray-300 bg-gray-50 object-contain"
            />
          ) : null}
          <div>
            <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
              Organization
            </p>
            <h1 className="mt-sp-1 text-fs-h1 font-medium text-gray-900">
              {org.data?.display_name ?? '…'}
            </h1>
            <p className="mt-sp-1 text-fs-meta text-gray-700">
              {org.data?.user_count ?? '—'} users · org id{' '}
              <code className="font-mono">{org.data?.id ?? '—'}</code>
            </p>
          </div>
        </div>
        {!editing ? (
          <Gated capability="org.edit">
            <PrimaryButton label="Edit settings" onClick={() => setEditing(true)} />
          </Gated>
        ) : null}
      </header>

      {editing ? (
        <Gated capability="org.edit">
          <form className="flex flex-col gap-sp-6" onSubmit={submit}>
            <details
              open
              className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
            >
              <summary className="cursor-pointer text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
                Branding
              </summary>
              <div className="mt-sp-4 flex flex-col gap-sp-4">
                <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
                  Display name
                  <input
                    required
                    maxLength={80}
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-body text-gray-900"
                  />
                </label>
                <FileToDataURL
                  label="Logo"
                  value={logo}
                  onChange={setLogo}
                />
              </div>
            </details>

            <details className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-6">
              <summary className="cursor-pointer text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
                Defaults
              </summary>
              <div className="mt-sp-4 grid gap-sp-4 sm:grid-cols-2">
                <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
                  Timezone
                  <input
                    placeholder="America/Los_Angeles"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-body text-gray-900"
                  />
                  <span className="text-fs-meta text-gray-700">IANA TZ identifier.</span>
                </label>
                <label className="flex flex-col gap-sp-1 text-fs-body text-gray-900">
                  Locale
                  <input
                    placeholder="en-US"
                    value={locale}
                    onChange={(e) => setLocale(e.target.value)}
                    className="rounded-r-default border border-gray-300 bg-gray-50 px-sp-2 py-sp-1 text-fs-body text-gray-900"
                  />
                  <span className="text-fs-meta text-gray-700">BCP-47 language tag.</span>
                </label>
              </div>
            </details>

            <details className="rounded-r-card border border-gray-300 bg-gray-50 p-sp-6">
              <summary className="cursor-pointer text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
                Feature flags
              </summary>
              <ul className="mt-sp-4 flex flex-col gap-sp-3">
                {knownFlagPairs.map((f) => (
                  <li key={f.key} className="flex items-start gap-sp-3">
                    <input
                      id={`flag-${f.key}`}
                      type="checkbox"
                      checked={f.enabled}
                      onChange={(e) =>
                        setFlags((prev) => ({ ...prev, [f.key]: e.target.checked }))
                      }
                      className="mt-sp-1"
                    />
                    <label htmlFor={`flag-${f.key}`} className="flex flex-col gap-sp-1">
                      <span className="text-fs-body text-gray-900">{f.label}</span>
                      <span className="text-fs-meta text-gray-700">{f.help}</span>
                    </label>
                  </li>
                ))}
              </ul>
            </details>

            <div className="flex items-center gap-sp-3">
              <PrimaryButton
                type="submit"
                label={patch.isPending ? 'Saving…' : 'Save settings'}
                disabled={patch.isPending}
              />
              <SecondaryButton type="button" label="Cancel" onClick={cancel} />
              {patch.isError ? (
                <span role="alert" className="text-fs-meta text-critical">
                  {String(patch.error)}
                </span>
              ) : null}
            </div>
          </form>
        </Gated>
      ) : (
        <section
          aria-label="Current settings"
          className="flex flex-col gap-sp-4 rounded-r-card border border-gray-300 bg-gray-50 p-sp-6"
        >
          <ReadRow label="Display name" value={org.data?.display_name ?? '—'} />
          <ReadRow label="Timezone" value={org.data?.timezone || 'using system default'} />
          <ReadRow label="Locale" value={org.data?.locale || 'using system default'} />
          <div>
            <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
              Feature flags
            </p>
            <ul className="mt-sp-2 flex flex-wrap gap-sp-2 font-mono text-fs-meta">
              {KNOWN_FLAGS.map((f) => {
                const enabled = !!(org.data?.feature_flags ?? {})[f.key];
                return (
                  <li
                    key={f.key}
                    className={
                      enabled
                        ? 'rounded-r-chip bg-healthy-bg px-sp-2 py-sp-1 text-healthy-fg'
                        : 'rounded-r-chip border border-gray-300 px-sp-2 py-sp-1 text-gray-700'
                    }
                  >
                    {enabled ? '✓ ' : '· '}
                    {f.key}
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      )}
    </div>
  );
}

function ReadRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-sp-3">
      <p className="text-fs-eyebrow font-medium uppercase tracking-[0.14em] text-gray-700">
        {label}
      </p>
      <p className="text-fs-body text-gray-900">{value}</p>
    </div>
  );
}
